/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCurrentUser } from "actions/CurrentUserActions";
import {
  FIREFLY_PRO_OFFER_ID_DIRECT_TO_PAID,
  FIREFLY_PRO_OFFER_ID_TRIAL_TO_PAID,
} from "constants/Commerce";

// Adobe Commerce/AOS offer ID for the Firefly Pro plan, switched by the
// fireflyProTrialToPaidEnabled CurrentUser flag so we can test the
// Trial-to-Paid offer on a subset of users while most traffic keeps buying
// the existing offer. Shared by usePremiumPricing (display price) and
// useCheckout (purchase) so they always quote and charge the same SKU.
// Returns null until CurrentUser has loaded, since defaulting to either SKU
// before then risks quoting or charging the wrong one.
export const useOfferId = (): string | null => {
  const { anonymousAllowedCurrentUser, isCurrentUserLoaded } = useCurrentUser();

  if (!isCurrentUserLoaded) {
    return null;
  }

  return anonymousAllowedCurrentUser?.fireflyProTrialToPaidEnabled
    ? FIREFLY_PRO_OFFER_ID_TRIAL_TO_PAID
    : FIREFLY_PRO_OFFER_ID_DIRECT_TO_PAID;
};
